import { useEffect, useState } from 'react';
import { fetchJSON, patchJSON } from '../api';
import { useProject } from '../context/ProjectContext';
import { ConfirmDialog } from '@/components/ui';

const STAGE_ORDER = ['collection', 'qa_review', 'reconciliation', 'final_delivery'];
const NEXT_LABELS = {
  collection: 'Advance to QA Review',
  qa_review: 'Advance to Reconciliation',
  reconciliation: 'Advance to Final Delivery',
};
const PREV_LABELS = {
  qa_review: 'Back to Collection',
  reconciliation: 'Back to QA Review',
  final_delivery: 'Back to Reconciliation',
};
// User-facing consequences when reverting from a stage. Surfaced in the
// confirm dialog so PMs don't fat-finger a destructive workflow regression.
const REVERT_WARNINGS = {
  qa_review: 'This will lock QA Review, Conflicts, Deferred, Reconciliation, and Final Delivery for everyone on this project until you advance back. Use this only if surveyors need to keep collecting.',
  reconciliation: 'This will lock Reconciliation and Final Delivery, and unlock QA Review for further edits. PM/QA work in those stages will be paused.',
  final_delivery: 'This will reopen Reconciliation. The deliverable is no longer signed off.',
};

export default function WorkflowBar({ visibleAt }) {
  const { selected, refreshProjects } = useProject();
  const [advancing, setAdvancing] = useState(false);
  const [error, setError] = useState(null);
  const [qaStats, setQaStats] = useState(null);
  const [confirmRevertTo, setConfirmRevertTo] = useState(null);

  const stage = selected?.workflow_stage || 'collection';
  const stageIdx = STAGE_ORDER.indexOf(stage);

  // Fetch QA completion when on qa_review stage
  useEffect(() => {
    if (stage === 'qa_review' && visibleAt === 'qa_review' && selected) {
      fetchJSON(`/${selected.id}/qa-completion`)
        .then(setQaStats)
        .catch(() => {});
    }
  }, [stage, visibleAt, selected]);

  // Only show on the correct page
  if (stage !== visibleAt) return null;

  const nextStage = stageIdx < STAGE_ORDER.length - 1 ? STAGE_ORDER[stageIdx + 1] : null;
  const prevStage = stageIdx > 0 ? STAGE_ORDER[stageIdx - 1] : null;

  const needsForce = stage === 'qa_review' && qaStats && qaStats.pct < 90;

  const changeStage = async (newStage, force = false) => {
    setAdvancing(true);
    setError(null);
    try {
      const body = { stage: newStage };
      if (force) body.force = true;
      await patchJSON(`/${selected.id}/workflow-stage`, body);
      await refreshProjects();
    } catch (err) {
      setError(err.message || 'Failed to change stage');
    } finally {
      setAdvancing(false);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 mb-6">
      {/* QA completion stats */}
      {stage === 'qa_review' && qaStats && (
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500">
              QA Review: {qaStats.pct}% complete ({qaStats.approved}/{qaStats.total} approved)
            </span>
            {qaStats.pct >= 90 && (
              <span className="text-[10px] text-emerald-600 font-medium">Ready to advance</span>
            )}
            {qaStats.pct < 90 && (
              <span className="text-[10px] text-amber-600 font-medium">Below 90% threshold</span>
            )}
          </div>
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                qaStats.pct >= 90 ? 'bg-emerald-500' : 'bg-amber-500'
              }`}
              style={{ width: `${Math.min(qaStats.pct, 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {prevStage && (
            <button
              onClick={() => setConfirmRevertTo(prevStage)}
              disabled={advancing}
              className="text-xs px-2 py-1 rounded text-amber-700 border border-amber-200 bg-amber-50 hover:bg-amber-100 transition-colors disabled:opacity-50"
              title="Reverts the project to a previous stage — locks tools for this stage and all later ones"
            >
              &larr; {PREV_LABELS[stage]}
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {error && <span className="text-xs text-red-600">{error}</span>}
          {nextStage && (
            <button
              onClick={() => changeStage(nextStage, needsForce)}
              disabled={advancing}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                needsForce
                  ? 'bg-amber-600 text-white hover:bg-amber-500'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              {advancing
                ? 'Updating...'
                : needsForce
                  ? 'Force Advance'
                  : NEXT_LABELS[stage]}
              {!advancing && <span>&rarr;</span>}
            </button>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmRevertTo}
        title={`Revert workflow to ${confirmRevertTo?.replace('_', ' ')}?`}
        description={REVERT_WARNINGS[stage] || 'This rolls the project back to an earlier stage.'}
        confirmLabel="Revert stage"
        cancelLabel="Keep current stage"
        destructive
        onConfirm={async () => {
          await changeStage(confirmRevertTo);
        }}
        onClose={() => setConfirmRevertTo(null)}
      />
    </div>
  );
}
